import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import BarChartOutlined from '@mui/icons-material/BarChartOutlined'
import DriveFileRenameOutlineOutlined from '@mui/icons-material/DriveFileRenameOutlineOutlined'

import {
  deleteAction,
  getActionbyRisque,
} from '../../redux/actions/actionActions'
import { getRisquebyId } from '../../redux/risques/risqueActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { formatTitle } from '../../utils/formatTitle'
import { writeEntityNav } from '../../utils/entityNav'
import UseAlert from '../../components/common/alert'
import {
  DetailAccordion,
  DetailButton,
  DetailInfoGrid,
  DetailInfoItem,
  DetailPageHead,
  RelatedActionsPanel,
} from '../../components/common/detail'
import {
  ListPagePill,
  RISK_HORIZON_PILL,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import '../../components/common/ListPageTable.css'

export default function RisqueDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)

  const actionsList = useSelector(
    (state: RootState) => state.action.actionsbyRisque
  )
  const risque = useSelector((state: RootState) => state.risque.risque)
  const loading = useSelector((state: RootState) => state.action.loading)

  const [open, setOpen] = useState({ info: true, actions: true })
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  useEffect(() => {
    if (id) {
      dispatch(getActionbyRisque(id))
      dispatch(getRisquebyId(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (!risque?._id) return
    writeEntityNav({
      riskId: risque._id,
      riskLabel: formatTitle(risque.RisqueName || ''),
      missionId: risque.MissionId || undefined,
      missionLabel: risque.MissionName
        ? formatTitle(risque.MissionName)
        : undefined,
      planId: (risque as any).PlanId || undefined,
      planLabel: risque.PlanName ? formatTitle(risque.PlanName) : undefined,
    })
  }, [risque])

  const handleDeleteClick = async (ActionId: string) => {
    try {
      await dispatch(deleteAction(ActionId))
      setAlert({
        open: true,
        message: t('alerts.onDelete.success', {
          defaultValue: 'Deleted successfully',
        }),
        severity: 'success',
      })
      if (id) dispatch(getActionbyRisque(id))
    } catch {
      setAlert({
        open: true,
        message: t('alerts.onDelete.error', {
          defaultValue: 'Error deleting. Please try again later.',
        }),
        severity: 'error',
      })
    }
  }

  const canUpdate =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)
  const canAnalyze =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.DSSI) ||
    hasRole(roles, UserRole.ADMIN)

  const title = formatTitle(risque?.RisqueName || '') || t('Risk')
  const rows = Array.isArray(actionsList) ? actionsList : []
  const priority = Number(risque?.Priority)
  const prioStyle = RISK_HORIZON_PILL[priority] ?? RISK_HORIZON_PILL[3]

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
      />

      <div className="detail-page">
        <DetailPageHead
          eyebrow={t('riskDetailsPage.eyebrow', {
            defaultValue: 'Management · Risks',
          })}
          title={title}
          subtitle={t('riskDetailsPage.subtitle', {
            defaultValue:
              'Risk details and treatment actions linked to this risk.',
          })}
          actions={
            <>
              {canAnalyze ? (
                <DetailButton
                  variant="ghost"
                  onClick={() =>
                    navigate(`/risque-detail/view-analysis/${id}`)
                  }
                >
                  <BarChartOutlined sx={{ fontSize: 16 }} />
                  {t('buttons.viewAnalysis')}
                </DetailButton>
              ) : null}
              {canUpdate ? (
                <DetailButton
                  onClick={() => navigate(`/risque/${risque?._id}`)}
                >
                  <DriveFileRenameOutlineOutlined sx={{ fontSize: 16 }} />
                  {t('Update Risk', { defaultValue: 'Update Risk' })}
                </DetailButton>
              ) : null}
            </>
          }
        />

        <DetailAccordion
          title={t('Information', { defaultValue: 'Information' })}
          open={open.info}
          onToggle={() => setOpen((p) => ({ ...p, info: !p.info }))}
        >
          <DetailInfoGrid>
            <DetailInfoItem label={t('Name', { defaultValue: 'Name' })} value={title} />
            <DetailInfoItem
              label={t('Description', { defaultValue: 'Description' })}
              value={
                <ListPageDescription value={(risque as any)?.Description} />
              }
            />
            <DetailInfoItem
              label={t('Priority')}
              value={
                Number.isFinite(priority) && priority > 0 ? (
                  <ListPagePill
                    label={t(`prioritRisk.${priority}`, {
                      defaultValue: String(priority),
                    })}
                    bg={prioStyle.bg}
                    color={prioStyle.color}
                  />
                ) : (
                  '—'
                )
              }
            />
            <DetailInfoItem
              label={t('Mission', { defaultValue: 'Mission' })}
              value={
                risque?.MissionName
                  ? formatTitle(risque.MissionName)
                  : '—'
              }
            />
            <DetailInfoItem
              label={t('Plan', { defaultValue: 'Plan' })}
              value={
                risque?.PlanName ? formatTitle(risque.PlanName) : '—'
              }
            />
          </DetailInfoGrid>
        </DetailAccordion>

        <RelatedActionsPanel
          title={t('Tasks', { defaultValue: 'Actions' })}
          listTitle={t('tasks_list', { defaultValue: 'Task list' })}
          emptyMessage={t('riskDetailsPage.empty.actions', {
            defaultValue: 'No action is linked to this risk yet.',
          })}
          rows={rows}
          loading={loading}
          loadingDeps={[id]}
          roles={roles}
          open={open.actions}
          onToggle={() => setOpen((p) => ({ ...p, actions: !p.actions }))}
          onDelete={handleDeleteClick}
        />
      </div>
    </>
  )
}
